import { ActivityEntry } from '../components/activity-log/activity-log.types';
import { documentKindLabelKeyOf } from './document-ai-state';
import { feedbackKeyOf, isoDateLabel } from './document-feedback-keys';

/**
 * REV-21869 (#165, AP8d) — the mapping `RunTrailEntry → ActivityEntry`, ADR-0018 interim.
 *
 * A pure presenter, same shape as `balance-sheet.presenter.ts`: no i18n injection, no HTTP, no
 * store. The caller (the page, or the component-lab view) passes in `translate` so the SAME
 * function runs against the SAME fixture data on both sides — the lab tests the mapping, not a
 * second, hand-built approximation of it (contract-r2.md S8).
 */

/** `TrailEntryKind` — `trail.ts:16-20` in the ai-worktree. */
export type TrailEntryKind = 'OK' | 'ATTENTION' | 'AI_FINDING';

/**
 * The wire shape this presenter needs — a narrowed kernel `RunTrailEntry` (`types/lib/models/api/
 * trail.ts`). A line carries an i18n key and its parameters, never prose and never an amount of its
 * own: a value the run read travels as `amountMinor` + `currency` in `params`.
 */
export interface RunTrailEntry {
    sequence: number;
    at: string;
    accountId: string | null;
    documentId?: string | null;
    /** `RUN_STARTED`, `DOCUMENT_READ`, `DOCUMENT_UNREADABLE`, `COMPARED`, `RUN_FAILED`. */
    step?: string;
    kind: TrailEntryKind;
    titleKey: string;
    params?: Record<string, string | number>;
}

/** `translate(key, params)` — an i18next `t`, narrowed to the one overload this presenter uses. */
export type TrailTranslate = (key: string, params?: Record<string, unknown>) => string;

/** How an amount from the trail is shown; the account page passes the app's number format. */
export type TrailMoneyFormat = (amountMinor: number, currency: string) => string;

/** Two decimals with Swiss grouping — the format of the screens, for callers without the app's pipe. */
const SWISS_AMOUNT = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatTrailMoney: TrailMoneyFormat = (amountMinor, currency) =>
    `${currency} ${SWISS_AMOUNT.format(amountMinor / 100)}`;

/** What started a run, as the trail line names it. */
export const TRAIL_TRIGGER_LABEL_KEYS: Readonly<Record<string, string>> = {
    DOCUMENT_ASSIGNED: 'abcp_trail_trigger_document_assigned',
    DOCUMENT_UNASSIGNED: 'abcp_trail_trigger_document_unassigned',
    DOCUMENT_REMOVED: 'abcp_trail_trigger_document_removed',
    ERP_SYNC: 'abcp_trail_trigger_erp_sync',
};

/** The comparison's outcome, named like the status chip on the account card. */
export const TRAIL_OUTCOME_LABEL_KEYS: Readonly<Record<string, string>> = {
    MATCHED: 'abcp_accounts_status_reconciled',
    DIFFERENCE: 'abcp_accounts_status_difference',
    OPEN: 'abcp_accounts_status_open',
};

/**
 * A line's parameters as its sentence names them: the document kind as its label, the amount with its
 * currency, the date as dd.MM.yyyy, the trigger and the outcome in words. Identifiers never reach the
 * sentence; the raw parameters stay for `rawDetail`.
 */
export function trailDisplayParams(
    params: Record<string, string | number> | undefined,
    translate: TrailTranslate,
    formatMoney: TrailMoneyFormat = formatTrailMoney,
): Record<string, string | number> {
    const raw = params ?? {};
    const shown: Record<string, string | number> = { ...raw };
    const wordOf = (map: Readonly<Record<string, string>>, value: string | number | undefined): string => {
        const key = feedbackKeyOf(map, value == null ? undefined : String(value));
        return key === '' ? '—' : translate(key);
    };
    if ('documentKind' in raw) {
        const key = documentKindLabelKeyOf(String(raw['documentKind']));
        shown['kind'] = key === '' ? '—' : translate(key);
    }
    if (typeof raw['amountMinor'] === 'number' && raw['currency'] != null) {
        shown['amount'] = formatMoney(raw['amountMinor'], String(raw['currency']));
    }
    if ('date' in raw) {
        shown['date'] = isoDateLabel(String(raw['date']));
    }
    if ('trigger' in raw) {
        shown['trigger'] = wordOf(TRAIL_TRIGGER_LABEL_KEYS, raw['trigger']);
    }
    if ('outcome' in raw) {
        shown['outcome'] = wordOf(TRAIL_OUTCOME_LABEL_KEYS, raw['outcome']);
    }
    return shown;
}

/**
 * The pane's own cap on rows loaded across pages (REV-21869 #165, Runde 3 / SRR F6).
 *
 * The service paginates `nextSequence` until it answers `null`; a run over ten years of closings
 * must not turn "load more" into an infinite loop. This is where a follow-up ticket would hang a
 * real "load more" — not the accident of the first page.
 */
export const TRAIL_MAX_ROWS = 500;

/**
 * The row rule (Runde 2 / A-C3) — what an account's pane shows out of a run's whole trail:
 *
 *     alle Zeilen mit accountId === <dieses Konto>
 *   + alle Zeilen ohne accountId, deren kind === ATTENTION      (Lauf-Warnungen)
 *   − alles andere                                              (Planungszeilen, fremde Konten)
 *
 * A run-level warning belongs to every account's pane — the protocol is the only channel besides
 * the status column (prototype §5), and a swallowed warning is a lost one. A planning line says
 * nothing about any one account and would be noise at the top of every pane in the closing.
 */
export function selectAccountTrailEntries(entries: RunTrailEntry[], accountId: string): RunTrailEntry[] {
    return entries.filter((entry) => entry.accountId === accountId || (entry.accountId === null && entry.kind === 'ATTENTION'));
}

/** Below this the label is relative, above it absolute with the year (CPO decision D5). */
const RELATIVE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const RELATIVE = new Intl.RelativeTimeFormat('de-CH', { numeric: 'auto' });

/** Largest unit first — the first one that fits is the one a person would say. */
const RELATIVE_UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
    ['day', 24 * 60 * 60 * 1000],
    ['hour', 60 * 60 * 1000],
    ['minute', 60 * 1000],
];

/**
 * `at` (ISO, UTC) → the label the entry shows, in Europe/Zurich.
 *
 * **CPO decision D5:** relative under seven days („vor 2 Stunden"), absolute **with the year** beyond
 * („24.04.2026 12:00"). Both halves of the log go through here, so the two sources cannot end up
 * labelled differently in one list.
 *
 * ⚠️ **This used to return `DD.MM. HH:mm` unconditionally**, which is neither half of D5 — found in
 * the round-1 review. Three comments in the codebase asserted the relative behaviour, and the e2e
 * only checked that no ISO string reached the screen, so nothing went red. The year mattered most:
 * a log kept for ten years showed „24.04." with no way to tell which April.
 *
 * The pure view is unaffected: it takes `timeLabel` as a ready string and renders it
 * (`activity-log.types.ts`), and the component lab feeds its own literals — so the accepted
 * REV-21366 contract does not depend on this function.
 *
 * `Intl` carries the DST rule and the German plural forms itself; a single well-known zone needs no
 * timezone library.
 */
export function formatTrailTimeLabel(at: string, now: Date = new Date()): string {
    const instant = new Date(at);
    const elapsed = now.getTime() - instant.getTime();

    // Only the PAST is relative. A clock skew that puts an entry slightly in the future must not read
    // „in 3 Minuten" on a closing's audit trail — it falls through to the absolute form.
    if (elapsed >= 0 && elapsed < RELATIVE_WINDOW_MS) {
        for (const [unit, size] of RELATIVE_UNITS) {
            const amount = Math.floor(elapsed / size);
            if (amount >= 1) {
                return RELATIVE.format(-amount, unit);
            }
        }

        return RELATIVE.format(0, 'minute');
    }

    const parts = new Intl.DateTimeFormat('de-CH', {
        timeZone: 'Europe/Zurich',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(instant);
    const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';

    return `${part('day')}.${part('month')}.${part('year')} ${part('hour')}:${part('minute')}`;
}

/**
 * One `RunTrailEntry` → one `ActivityEntry`.
 *
 * A-C1: `kind` never becomes `actor.type`. Every trail line was written by the run, so every one
 * is `'agent'` — an ATTENTION line shown as `'user'` would claim a human acted, in a protocol that
 * stands for ten years (`RUN_TRAIL_RETENTION_YEARS`). `kind` gets its own carrier instead
 * (`ActivityEntry.kind`, rendered as `data-kind`) so the attention line is not a lost signal.
 *
 * Runde 7 — the actor is also NAMED. The pane falls back to `abcp_activity_actor_unknown`
 * ("Unbekannt") whenever `actor.name` is empty (`activity-log.component.html:56`), and the demo
 * run showed that word beside every line of the protocol. It is the wrong word here: the wire
 * carries no actor field (`ai-abcp-run-trail.interface.ts:30-40`), but the author is not unknown —
 * the run wrote the line, which is exactly what `type: 'agent'` above already asserts. The
 * fallback stays where it belongs: an entry whose author was never recorded (a user comment
 * without a name). The name is deliberately generic, see `abcp_activity_actor_agent`.
 *
 * A per-engine name is possible in principle and deliberately NOT done here: the full wire shape
 * carries `usedEntry.name` (`types/lib/models/api/trail.ts:67`) and `abcp.catalog.*` already holds
 * wordings for those names — but only some lines have one (the refusal mark sets `usedEntry: null`
 * by construction), so it is a second, partial source and belongs in its own slice.
 */
export function mapTrailEntryToActivity(
    entry: RunTrailEntry,
    translate: TrailTranslate,
    formatMoney: TrailMoneyFormat = formatTrailMoney,
): ActivityEntry {
    return {
        id: `trail-${entry.sequence}`,
        actor: { type: 'agent', name: translate('abcp_activity_actor_agent') },
        title: translate(entry.titleKey, trailDisplayParams(entry.params, translate, formatMoney)),
        timeLabel: formatTrailTimeLabel(entry.at),
        kind: entry.kind,
        rawDetail: rawDetailOf(entry.titleKey, null, entry.params),
    };
}

/** The entry's raw codes — keys and parameters as the service sent them. */
export function rawDetailOf(
    titleKey: string,
    detailKey: string | null | undefined,
    params: Record<string, string | number> | null | undefined,
): string {
    const parts = [titleKey, ...(detailKey ? [detailKey] : [])];
    for (const [key, value] of Object.entries(params ?? {})) {
        parts.push(`${key}=${value}`);
    }
    return parts.join(' · ');
}

/** The row rule, then the mapping — in trail order (`sequence` is strictly monotonic). */
export function mapTrailToActivityEntries(entries: RunTrailEntry[], accountId: string, translate: TrailTranslate): ActivityEntry[] {
    return selectAccountTrailEntries(entries, accountId).map((entry) => mapTrailEntryToActivity(entry, translate));
}
